import {
    SHOW_EVENTS_FLOW,
    SHOW_HOURLY_FLOW,
    SHOW_MONTHLY_FLOW,
    SHOW_PREPAID_FLOW,
    SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE,
    SHOW_HOURLY_FLOW_PARK_NOW,
} from '@booking/constants'
import featureToggles from 'shared/featureToggles'
import { SEARCH_MODE_EVENTS, SEARCH_MODE_HOURLY, SEARCH_MODE_MONTHLY, SEARCH_MODE_PREPAID } from 'search/constants'

export const getSwitchButtons = (
    defaultCustomerFlow,
    parkingMode,
    onClickSwitchTo,
    currentCustomerFlowType,
    onSwitchHourlyType,
    showOnlyBookInAdvanceFlow
) => {
    if (!defaultCustomerFlow) return []

    const defaultCustomerFlowArr = defaultCustomerFlow?.length > 0 ? defaultCustomerFlow?.split('|') : []
    const isHourly = parkingMode === SEARCH_MODE_HOURLY
    const isBookInAdvance = isHourly && currentCustomerFlowType === SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE
    const isParkNow = isHourly && currentCustomerFlowType === SHOW_HOURLY_FLOW_PARK_NOW

    const isMonthly = parkingMode === SEARCH_MODE_MONTHLY
    const isEventFlow = parkingMode === SEARCH_MODE_EVENTS && featureToggles.hasEventFlow
    const isPrepaidFlow = parkingMode === SEARCH_MODE_PREPAID && featureToggles.hasPrepaidFlow

    const handleHourlyClick = hourlyType => {
        if (parkingMode !== SEARCH_MODE_HOURLY) {
            onClickSwitchTo(SEARCH_MODE_HOURLY)
        }
        onSwitchHourlyType(hourlyType)
    }

    const switchOptions = [
        {
            component: {
                label: <T>booking.switch.hourly</T>,
                onClick: () => handleHourlyClick(SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE),
                active: isBookInAdvance,
            },
            condition: [SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE],
        },
        {
            component: {
                label: <T>booking.switch.monthly</T>,
                onClick: () => onClickSwitchTo(SEARCH_MODE_MONTHLY),
                active: isMonthly,
            },
            condition: SHOW_MONTHLY_FLOW,
        },
        {
            component: {
                label: <T>booking.switch.park.now</T>,
                onClick: () => handleHourlyClick(SHOW_HOURLY_FLOW_PARK_NOW),
                active: isParkNow,
            },
            condition: [SHOW_HOURLY_FLOW_PARK_NOW],
        },
        {
            ...(featureToggles.hasPrepaidFlow
                ? {
                      component: {
                          label: <T>booking.switch.prepaid</T>,
                          onClick: () => onClickSwitchTo(SEARCH_MODE_PREPAID),
                          active: isPrepaidFlow,
                      },
                      condition: SHOW_PREPAID_FLOW,
                  }
                : {}),
        },
        {
            ...(featureToggles.hasEventFlow
                ? {
                      component: {
                          label: <T>booking.switch.event</T>,
                          onClick: () => onClickSwitchTo(SEARCH_MODE_EVENTS),
                          active: isEventFlow,
                      },
                      condition: [SHOW_EVENTS_FLOW],
                  }
                : {}),
        },
    ]

    const buttonsToRender = []

    const priorityOrder = [
        SHOW_HOURLY_FLOW_PARK_NOW,
        SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE,
        SHOW_EVENTS_FLOW,
        SHOW_MONTHLY_FLOW[0],
        SHOW_PREPAID_FLOW[0],
    ]
    const sortOptions = () => {
        if (showOnlyBookInAdvanceFlow) {
            return switchOptions.filter(option => option.condition && option.condition.includes(SHOW_HOURLY_FLOW[1]))
        }

        const matchingOptions = switchOptions.filter(
            option => option.condition && defaultCustomerFlowArr?.some(element => option.condition.includes(element))
        )

        matchingOptions.sort((a, b) => {
            const aType = a.condition[0]
            const bType = b.condition[0]

            return priorityOrder.indexOf(aType) - priorityOrder.indexOf(bType)
        })
        matchingOptions.forEach(element => {
            buttonsToRender.push(element.component)
        })
    }

    if (isHourly || isParkNow || isEventFlow) {
        sortOptions()
    } else {
        for (const option of switchOptions) {
            if (defaultCustomerFlowArr?.some(element => option.condition?.includes(element))) {
                buttonsToRender.push(option.component)
            }
        }
    }
    return buttonsToRender
}

export const validationErrorsInitialState = {
    selectCardError: false,
    selectRateError: false,
    selectedVehiclesError: false,
    deliveryAddressError: false,
}

export const getBookingValidationErrors = ({
    rate,
    selectedCreditCard,
    selectedVehicles,
    deliveryAddress,
    parkingMode,
    purchaseValidation = false,
} = {}) => {
    let isValid = true
    let errors = {
        ...validationErrorsInitialState,
    }

    if (!rate) {
        errors = {
            ...errors,
            selectRateError: <T>booking.validation.select.rate</T>,
        }
        isValid = false
    }

    if (parkingMode === SEARCH_MODE_MONTHLY && !deliveryAddress) {
        errors = {
            ...errors,
            deliveryAddressError: <T>booking.validation.addDeliveryAddress</T>,
        }

        isValid = false
    }

    if (parkingMode !== SEARCH_MODE_PREPAID && selectedVehicles && selectedVehicles.length === 0) {
        errors = {
            ...errors,
            selectedVehiclesError: <T>booking.validation.select.vehicle</T>,
        }
        isValid = false
    }

    if (purchaseValidation) {
        if (!selectedCreditCard) {
            errors = {
                ...errors,
                selectCardError: <T>booking.validation.select.card</T>,
            }
            isValid = false
        }
    }

    return {
        errors,
        isValid,
    }
}
